import { App, Form, Input, Space } from "antd";
import ActionButton from "../../../masterComponents/actionButton";
import { useFetchData } from "../../../../hooks/useFetch/post";
import { useEffect, useMemo, useState } from "react";
import validator from "../../../../shared/validators/filterValueValidator";
import { useNavigate } from "react-router-dom";
import {
  OverriddenContainerWrapper,
  StyledCardPrompt,
  StyledContainerForm,
  StyledPrompt,
} from "./addNewRule.style";
import { useCommonPropertiesContext } from "../../../../contexts/commonPropertiesContext";
import "./addNewRule.css";
import ChoosedOptions from "./choosedOptions";
import { FaArrowLeft } from "react-icons/fa6";
import Icon from "../../../masterComponents/Icon";
import { PHRASES_DICTIONARY } from "../../../../entities/phrasesDictionary";
import { useAttrs } from "../../../../hooks/useEndpoints/useAttrs";

const AddNewRule = () => {
  const [attrInputValue, setAttrInputValue] = useState("");
  const [classInputValue, setClassInputValue] = useState("");

  const [choosedCharacteristics, setChoosedCharacteristics] = useState<
    { value: string; label: string }[]
  >([]);
  const [choosedClasses, setChoosedClasses] = useState<string[]>([]);

  const { TextArea } = Input;

  const navigate = useNavigate();

  const { classes } = useCommonPropertiesContext();

  const [form] = Form.useForm();

  const { message: messageApi } = App.useApp();

  const ruleName = Form.useWatch("ruleName", form);
  const ruleText = Form.useWatch("ruleText", form);

  const { attributes, isLoading: isLoadingAttributes } = useAttrs({
    classNames: choosedClasses ?? [],
    operation: "intersection",
    messageApi,
  });

  const [
    fetchAddNewRule,
    {
      isLoading: isLoadingAddNewRule,
      data: newRuleData,
      error: newRuleError,
      clearError: clearNewRuleError,
    },
  ] = useFetchData({
    query: "/rules/add",
    selectData: (data) => data,
  });

  useEffect(() => {
    if (newRuleData?.success && !isLoadingAddNewRule) {
      navigate("/rules");
    }
  }, [isLoadingAddNewRule]);

  useEffect(() => {
    if (newRuleError) {
      messageApi.error(newRuleError.message);
      clearNewRuleError?.();
    }
  }, [newRuleError, messageApi]);

  const attrsOptions = useMemo(
    () =>
      attributes?.map((attr) => ({
        value: attr.attr_code,
        label: attr.attr_descr,
      })) ?? [],
    [attributes],
  );

  useEffect(() => {
    if (!choosedCharacteristics.length) form.setFieldsValue({ ruleText: "" });
  }, [choosedCharacteristics]);

  const availableAttrCodes = useMemo(
    () => new Set(attributes?.map((a) => a.attr_code) ?? []),
    [attributes],
  );
  useEffect(() => {
    setChoosedCharacteristics((prev) =>
      prev.filter((item) => availableAttrCodes.has(item.value)),
    );
  }, [availableAttrCodes]);

  const ruleTextValid = useMemo(() => {
    if (!ruleText || choosedCharacteristics.length === 0) return false;

    const text = ruleText.toLowerCase();

    return choosedCharacteristics.every((ch) =>
      text.includes(String(ch.value).toLowerCase()),
    );
  }, [choosedCharacteristics, ruleText]);

  const formValid = ruleName && choosedCharacteristics.length && ruleTextValid;

  return (
    <OverriddenContainerWrapper>
      <ActionButton
        text="Back"
        icon={<Icon icon={FaArrowLeft} />}
        className="btn-color"
        styles={{
          position: "absolute",
          top: "var(--spacing-sm)",
          left: "var(--spacing-sm)",
        }}
        onClick={() => navigate("/rules")}
      />
      <StyledContainerForm>
        <div style={{ width: "70%" }}>
          <Form form={form} layout="vertical">
            <Form.Item label="Rule name" name="ruleName" required>
              <Input
                onBlur={(e) => {
                  const trimmed = validator.validate(e.target.value);
                  form.setFieldsValue({ ruleName: trimmed });
                }}
              />
            </Form.Item>
            <Form.Item label="Classification" name="classification">
              <ChoosedOptions
                valueFormat="string"
                name={"Choose Classification"}
                inputValue={classInputValue}
                setInputValue={setClassInputValue}
                options={classes.map((opt) => ({ value: opt }))}
                choosedValues={choosedClasses}
                setChoosedValues={setChoosedClasses}
              />
            </Form.Item>

            <Form.Item
              label="Target Characteristics"
              name="targetCharacteristics"
              required
            >
              <ChoosedOptions
                valueFormat="value-label"
                name={"Choose Characteristics"}
                inputValue={attrInputValue}
                setInputValue={setAttrInputValue}
                options={attrsOptions}
                choosedValues={choosedCharacteristics}
                setChoosedValues={setChoosedCharacteristics}
                isLoadingAttributes={isLoadingAttributes}
              />
            </Form.Item>

            <Form.Item label="Rule text" name="ruleText" required>
              <TextArea
                autoSize={{ minRows: 1 }}
                status={!ruleTextValid && ruleText && "error"}
                onBlur={(e) => {
                  const trimmed = validator.validate(e.target.value);
                  form.setFieldsValue({ ruleText: trimmed });
                }}
                disabled={!choosedCharacteristics.length}
              />
            </Form.Item>
          </Form>

          <div style={{ position: "relative" }}>
            <StyledPrompt
              $visible={!choosedCharacteristics.length}
              $color="rgba(0, 0, 0, 0.25)"
            >
              {PHRASES_DICTIONARY.PROMPTS_FOR_RULES.rule_text_prompt}
            </StyledPrompt>

            <StyledPrompt
              $visible={!ruleTextValid && ruleText}
              $color="#ff4d5090"
            >
              {PHRASES_DICTIONARY.PROMPTS_FOR_RULES.rule_text_prompt_error}
            </StyledPrompt>
          </div>

          <ActionButton
            text="Add New Rule"
            className="primary-btn-color"
            styles={{ marginTop: "var(--spacing-xs)" }}
            disabled={!formValid}
            onClick={() =>
              fetchAddNewRule(
                {},
                {
                  rule_name: ruleName,
                  rule_text: ruleText,
                  target_attrs: choosedCharacteristics.map((ch) => ch.value),
                },
              )
            }
          />
        </div>
        <Space
          direction="vertical"
          style={{ width: "30%", justifyContent: "center", display: "flex" }}
        >
          <StyledCardPrompt size="small">
            {PHRASES_DICTIONARY.PROMPTS_FOR_RULES.target_characteristics}
          </StyledCardPrompt>

          <StyledCardPrompt size="small">
            {PHRASES_DICTIONARY.PROMPTS_FOR_RULES.rule_text_description}
          </StyledCardPrompt>
          <StyledCardPrompt size="small">
            <span style={{ color: "red" }}>*</span> —{" "}
            {PHRASES_DICTIONARY.PROMPTS_FOR_RULES.required_filds}
          </StyledCardPrompt>
        </Space>
      </StyledContainerForm>
    </OverriddenContainerWrapper>
  );
};
export default AddNewRule;
